import { Button } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { useMemo } from "react";
import type { StandingsEntry } from "../../../tournament-formats/TournamentFormat";
import TournamentStandingsController from "./TournamentStandingsController";
import routeService from "services/RouteService";

interface DisplayEntry extends StandingsEntry {
  name: string;
  seed: number | null;
  division: number | undefined;
}

const RoundRobinStandings = () => {
  const controller = TournamentStandingsController.use();
  const { competition, entries, standingsData } = controller.state;
  const { divisions } = controller.playoffSettings;
  const showDivisionColumn = controller.showDivisionColumn;
  const canEditDivisions = controller.canEditPlayoffDivisions;
  if (!standingsData) return null;

  // Combine standings data with entry information to get player names
  const displayStandings = useMemo(() => {
    const result: Record<string, DisplayEntry[]> = {};

    // Process each group in the standings data
    Object.entries(standingsData.groups).forEach(([groupName, standingEntries]) => {
      result[groupName] = standingEntries
        .map((entry) => {
          // Find the tournament entry by ID
          const tournamentEntry = entries.findById(entry.entryId);

          // Get the name from the entry or use a fallback
          let name = "Unknown Player";
          let seed: number | null = null;
          let division: number | undefined;
          if (tournamentEntry) {
            name = tournamentEntry.shortNames.join(", ") || `Entry #${tournamentEntry.seed || "?"}`;
            seed = tournamentEntry.seed ?? null;
            division = tournamentEntry.division ?? undefined;
          }

          // Return the display entry with name and seed included
          return {
            ...entry,
            name,
            seed,
            division,
          };
        })
        .sort((a, b) => {
          if (b.points !== a.points) {
            return b.points - a.points;
          }
          const seedA = a.seed ?? Number.MAX_SAFE_INTEGER;
          const seedB = b.seed ?? Number.MAX_SAFE_INTEGER;
          return seedA - seedB;
        });
    });

    return result;
  }, [standingsData, controller.state.entries]);

  if (Object.keys(displayStandings).length === 0) {
    return <div className="py-4 text-center text-gray-500">No standings data available</div>;
  }

  return (
    <div className="space-y-6">
      {Object.entries(displayStandings).map(([groupName, groupEntries]) => (
        <div key={groupName} className="overflow-hidden border border-gray-200 rounded-md">
          <div className="px-4 py-2 font-medium text-blue-700 border-b border-gray-200 bg-blue-50">
            {groupName}
          </div>
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-200"
              data-testid={`standings-table-${groupName}`}
            >
              <thead className="bg-gray-50">
                <tr className="*:px-4 *:py-2">
                  <th
                    scope="col"
                    className="px-2! text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Pos
                  </th>
                  <th
                    scope="col"
                    className="px-2! text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Seed
                  </th>
                  <th
                    scope="col"
                    className="text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Played
                  </th>
                  <th
                    scope="col"
                    className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Won
                  </th>
                  <th
                    scope="col"
                    className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Lost
                  </th>
                  <th
                    scope="col"
                    className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Points
                  </th>
                  {showDivisionColumn && (
                    <th
                      scope="col"
                      className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                    >
                      Division
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupEntries.map((entry, index) => (
                  <tr key={entry.entryId} className="*:px-4 *:py-2 even:bg-white odd:bg-gray-50">
                    <td className="text-sm font-medium text-gray-900 whitespace-nowrap">{index + 1}</td>
                    <td className="text-sm text-center text-gray-500 whitespace-nowrap">
                      {entry.seed ?? "—"}
                    </td>
                    <td className="text-sm text-gray-900 whitespace-nowrap">
                      <Button
                        kind="link"
                        data-entry-id={entry.entryId}
                        onClick={() =>
                          routeService.openModal("/tournament/:id/entries/:entryId/edit", {
                            params: { id: competition?.tournament_session_id ?? "", entryId: entry.entryId },
                          })
                        }
                      >
                        {entry.name}
                      </Button>
                    </td>
                    <td className="text-sm text-center text-gray-500 whitespace-nowrap">{entry.played}</td>
                    <td className="text-sm text-center text-gray-500 whitespace-nowrap">{entry.wins}</td>
                    <td className="text-sm text-center text-gray-500 whitespace-nowrap">{entry.losses}</td>
                    <td
                      className={clsx(
                        "text-sm font-medium text-center whitespace-nowrap",
                        index === 0 ? "text-green-600" : "text-gray-900",
                      )}
                    >
                      {entry.points}
                    </td>
                    {showDivisionColumn && (
                      <td className="text-sm text-center text-gray-500 whitespace-nowrap">
                        <select
                          aria-label={`Playoff division for ${entry.name}`}
                          data-testid={`standings-division-${entry.entryId}`}
                          className="mixitone-select appearance-none inline-block w-auto min-h-0 rounded border border-gray-300 bg-white px-2 py-1 pr-7 text-xs leading-4"
                          value={entry.division?.toString() || ""}
                          disabled={!canEditDivisions}
                          title={
                            canEditDivisions
                              ? undefined
                              : controller.playoffsHaveStarted
                                ? "Playoff divisions cannot be changed after playoffs have started"
                                : undefined
                          }
                          onChange={(e) => {
                            const value = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
                            void controller.actionSetEntryDivision(entry.entryId, value);
                          }}
                        >
                          <option value="">None</option>
                          {Array.from({ length: divisions }, (_, i) => i + 1).map((divisionNum) => (
                            <option key={divisionNum} value={divisionNum.toString()}>
                              {divisionNum}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ApplicationView(RoundRobinStandings);
